import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Sun, CalendarDays, Pencil, RotateCcw } from "lucide-react";
import { Layout } from "@/components/Layout";
import { usePatientMode } from "@/lib/patientMode";
import { AG_KEY, AG_WEEK_KEY, TITRE, SOUS_TITRE, INTRO, todayKey, fmtDate, newDay, newWeek, estMinimisation, jourComplet } from "@/data/autograt";
import { Btn, ProgressBar } from "./agShared";
import { Rappel } from "@/components/Rappel";
import * as S from "./AutogratSteps";
import AutogratBilan from "./AutogratBilan";
import AutogratSemaine from "./AutogratSemaine";

const load = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || "null") ?? d; } catch { return d; } };
const STEPS = [
  { k: "difficile", label: "Ce qui a été difficile", C: S.StepDifficile },
  { k: "fonctionne", label: "Ce qui a fonctionné", C: S.StepFonctionne },
  { k: "effort", label: "Mes efforts", C: S.StepEffort },
  { k: "victoires", label: "Mes petites victoires", C: S.StepVictoires },
  { k: "ressources", label: "Ce que je reconnais chez moi", C: S.StepRessources },
  { k: "minimisation", label: "Est-ce que je minimise le positif ?", C: S.StepMinimisation },
  { k: "recadrage", label: "Laisser la réussite exister", C: S.StepRecadrage, when: (d) => estMinimisation(d.minimisation) },
  { k: "gratification", label: "Je choisis une autogratification", C: S.StepGratification },
  { k: "regle", label: "Ma règle d'autogratification", C: S.StepRegle, when: (d) => Boolean(d.gratification.cat) },
  { k: "phrase", label: "Ma phrase du jour", C: S.StepPhrase },
  { k: "scores", label: "Où j'en suis", C: S.StepScores },
];

export default function AutogratModule() {
  const { patientMode } = usePatientMode();
  const [days, setDays] = useState(() => load(AG_KEY, []));
  const [week, setWeek] = useState(() => load(AG_WEEK_KEY, newWeek()));
  const [phase, setPhase] = useState("accueil");
  const today = todayKey();
  const day = useMemo(() => days.find((d) => d.date === today) || newDay(today), [days, today]);
  useEffect(() => localStorage.setItem(AG_KEY, JSON.stringify(days)), [days]);
  useEffect(() => localStorage.setItem(AG_WEEK_KEY, JSON.stringify(week)), [week]);

  const set = (patch) => setDays((prev) => { const cur = prev.find((d) => d.date === today) || newDay(today); const next = { ...cur, ...patch, updatedAt: Date.now() }; return prev.some((d) => d.date === today) ? prev.map((d) => (d.date === today ? next : d)) : [...prev, next]; });
  const go = (p) => { setPhase(p); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const visible = STEPS.filter((s) => !s.when || s.when(day));
  const idx = visible.findIndex((s) => s.k === phase);
  const nav = { next: () => (idx < visible.length - 1 ? go(visible[idx + 1].k) : (set({ completed: true }), go("bilan"))), back: () => (idx > 0 ? go(visible[idx - 1].k) : go("accueil")) };
  const restart = () => { if (!window.confirm("Recommencer la journée d'aujourd'hui ? Vos réponses du jour seront effacées.")) return; setDays((prev) => prev.filter((d) => d.date !== today)); go("accueil"); };
  const started = jourComplet(day) || day.difficultes.length > 0;
  const Step = idx >= 0 ? visible[idx].C : null;

  return (
    <Layout>
      <div className="animate-fade-up" data-testid="ag-module" data-phase={phase}>
        {!patientMode && <Link to="/" className="text-sm text-[var(--gold)] hover:underline no-print" data-testid="ag-back-home">← Retour à l'accueil</Link>}
        <div className="mt-3 mb-5"><div className="text-xs uppercase tracking-[0.25em] font-bold text-[var(--gold)]">{TITRE}</div><p className="text-sm text-[var(--ink-soft)] mt-1">{SOUS_TITRE}</p></div>
        {Step && <ProgressBar current={idx + 1} total={visible.length} label={visible[idx].label} />}

        {phase === "accueil" && (
          <div className="rounded-3xl bg-white/80 border border-[var(--border)] p-6 sm:p-8" data-testid="ag-accueil">
            <div className="w-14 h-14 rounded-full bg-[#F3EADF] text-[var(--gold)] flex items-center justify-center mb-5"><Sun size={26} /></div>
            <h1 className="font-serif-title text-3xl sm:text-4xl text-[var(--ink)] leading-snug">{TITRE}</h1>
            <p className="text-[var(--ink)] leading-relaxed mt-4" data-testid="ag-intro">{INTRO}</p>
            <p className="text-sm text-[var(--ink-soft)] mt-2">Environ 3 à 5 minutes. Aucune question n'est obligatoire : vous pouvez passer une étape et y revenir.</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {!started ? <Btn onClick={() => go("difficile")} testid="ag-start">Commencer</Btn> : (
                <>
                  <Btn onClick={() => go(day.completed ? "bilan" : "difficile")} testid="ag-continue">{day.completed ? "Voir mon bilan du jour" : "Reprendre ma journée"}</Btn>
                  <Btn secondary onClick={() => go("difficile")} testid="ag-edit-day"><Pencil size={15} /> Modifier mes réponses</Btn>
                  <Btn secondary onClick={restart} testid="ag-restart"><RotateCcw size={15} /> Recommencer</Btn>
                </>
              )}
            </div>
            <div className="mt-6"><Rappel storageKey="m2hat_autograt_rappel" titre="Rappel du soir" texte="Quelques minutes pour regarder votre journée avec plus d'équilibre." defaultTime="20:00" testid="ag-rappel" /></div>
            {days.length > 0 && (
              <div className="mt-8 pt-6 border-t border-[var(--border)] flex flex-wrap items-center justify-between gap-3" data-testid="ag-history">
                <p className="text-sm text-[var(--ink-soft)]"><b className="text-[var(--ink)]" data-testid="ag-history-count">{days.length}</b> journée{days.length > 1 ? "s" : ""} enregistrée{days.length > 1 ? "s" : ""} sur cet appareil · dernière : {fmtDate([...days].sort((a, b) => (a.date < b.date ? 1 : -1))[0].date, { day: "numeric", month: "long" })}</p>
                <Btn secondary onClick={() => go("semaine")} testid="ag-open-week"><CalendarDays size={15} /> Ma semaine positive</Btn>
              </div>
            )}
          </div>
        )}

        {Step && <Step day={day} set={set} nav={nav} />}
        {phase === "bilan" && <AutogratBilan day={day} onEdit={(k) => go(k)} onFinish={() => go(days.length > 1 ? "semaine" : "accueil")} onRestart={restart} onWeek={() => go("semaine")} nbJours={days.length} />}
        {phase === "semaine" && <AutogratSemaine days={days} week={week} setWeek={setWeek} onBack={() => go("accueil")} />}
      </div>
    </Layout>
  );
}
